/**
 * use-accounts — reactive view over the web multi-account registry + the
 * switch/add/remove orchestration used by the account switcher UI.
 *
 * Switching an account is data-preserving: we set the active pointer, re-prime
 * the session actor for the new identity and let the PowerSync provider swap to
 * that user's DB instance (it reacts to the active user id). We NEVER call
 * `disconnectAndClear` on a switch — that would destroy the data. Only an
 * explicit "Sign out" / "Remove account" wipes a database.
 */

import { createSignal, onCleanup, onMount } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import * as routerPkg from "@tanstack/solid-router";
import { type AccountsState, getActiveAccount, type StoredAccount } from "@listto/shared";
import { ACCOUNTS_CHANGED_EVENT, accountsStore, readAccountsStateSync } from "@/lib/accounts";
import { resolveTabAccount } from "@/lib/active-account";
import {
  activeTabAccountId,
  clearTabAccountId,
  TAB_ACCOUNT_CHANGED_EVENT,
  writeTabAccountId,
} from "@/lib/tab-account";
import { pruneAliases } from "@/lib/account-alias";
import { primeSessionForAccount } from "@/lib/session-machine";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { queryClient } from "@/lib/query-client";

export function useAccounts() {
  const navigate = useNavigate();
  const routerPkgObj = routerPkg as {
    useRouter?: (opts?: { warn?: boolean }) => { state?: { matches?: { fullPath?: string }[] } };
  };
  const router =
    "useRouter" in routerPkgObj && typeof routerPkgObj.useRouter === "function"
      ? routerPkgObj.useRouter({ warn: false })
      : undefined;
  const { destroyAccountData } = usePowerSyncContext();
  const [state, setState] = createSignal<AccountsState>(readAccountsStateSync());

  // localStorage is the source of truth; reload on mount and on cross-tab
  // writes so a switch/add/remove in another tab is reflected here too.
  const reload = () => {
    void (async () => {
      setState(await accountsStore.load());
    })();
  };

  onMount(() => {
    reload();
    // Cross-tab: the `storage` event fires in OTHER tabs.
    const onStorage = (e: StorageEvent) => {
      if (e.key === "listto_accounts" || e.key === null) {
        reload();
      }
    };
    // Same-tab: `storage` never fires for our own writes, so a sign-out/remove in
    // THIS tab must be picked up via the registry's custom change event — else the
    // signed-out account lingers in the switcher until the next interaction.
    const onLocalChange = () => {
      reload();
    };
    globalThis.addEventListener("storage", onStorage);
    globalThis.addEventListener(TAB_ACCOUNT_CHANGED_EVENT, onLocalChange);
    globalThis.addEventListener(ACCOUNTS_CHANGED_EVENT, onLocalChange);
    onCleanup(() => {
      globalThis.removeEventListener("storage", onStorage);
      globalThis.removeEventListener(TAB_ACCOUNT_CHANGED_EVENT, onLocalChange);
      globalThis.removeEventListener(ACCOUNTS_CHANGED_EVENT, onLocalChange);
    });
  });

  const accounts = () => state().accounts;

  // Per-tab active account: which account is active in THIS tab lives in this
  // tab's `sessionStorage` pin, NOT in the registry's global `activeUserId`
  // (that is only a "last-used" default shared across tabs). The switcher MUST
  // highlight — and its switch-guard MUST compare against — the tab's own
  // account, or two tabs would both show the registry's last-active account as
  // "current" and the guard `userId === activeUserId()` would wrongly
  // short-circuit a real switch. `activeTabAccountId()` is reactive.
  const active = (): StoredAccount | null => resolveTabAccount(state(), activeTabAccountId());
  const activeUserId = () => active()?.userId ?? null;

  /**
   * Switches THIS TAB to a known account WITHOUT clearing any data, by rewriting
   * this tab's pin. Only this tab changes; other tabs keep their own account,
   * and NO URL changes — the address bar keeps naming the same resource. We
   * still keep the registry's `activeUserId` as the "last-used" default for
   * fresh tabs, but it does not drive which account is active in any given tab.
   *
   * Navigates home afterwards because the CURRENT view may address an entity
   * that belongs to the account we just left; landing on the dashboard is the
   * account-agnostic destination.
   */
  const switchTo = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    if (!next.accounts.some((a) => a.userId === userId)) {
      return;
    }
    // ORDER MATTERS. Drop the previous account's cached data and re-prime the
    // session machine FIRST, and flip the tab pin LAST:
    //
    // Flipping the pin is what makes every reactive consumer re-resolve — the
    // layout's PowerSync subscriptions re-run and push the new account's rows
    // into the TanStack cache. If `clear()` ran AFTER that flip it could wipe
    // that fresh push, and nothing would re-trigger it (the subscriptions only
    // re-run when what they track changes), leaving cache-only readers like the
    // dashboard empty forever.
    //
    // The old `/u/<n>` scheme got this ordering for free: the flip happened via
    // the navigation at the end. With the account out of the URL it has to be
    // explicit.
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    // Pin THIS tab (also remembers the device-wide default for fresh tabs).
    writeTabAccountId(userId);

    // D1 & D2: Preserve section route; fall back to section if on entity route ($)
    const matches = router?.state?.matches;
    const matchWithParam = matches?.findLast((m: { fullPath?: string }) =>
      Boolean(m.fullPath && m.fullPath.includes("$")),
    );
    if (matchWithParam?.fullPath) {
      const dollarIdx = matchWithParam.fullPath.indexOf("$");
      const prefix = matchWithParam.fullPath.slice(0, dollarIdx).replace(/\/+$/, "");
      const target = prefix.length > 0 ? prefix : "/";
      await (navigate as (opts: { to: string }) => Promise<void>)({ to: target });
    }
  };

  /**
   * Switches THIS TAB to another account and STAYS on the current URL.
   *
   * This is the move that only becomes possible once the account is out of the
   * URL: the address bar names a resource, so "look at this same thing as my
   * other account" is a pure identity change with no navigation. Used by the
   * "try another account" affordance on a not-found detail screen — the
   * local-first equivalent of Google Docs' account chooser.
   *
   * The bound PowerSync DB and every account-scoped query key derive from the
   * pin reactively, so the screen re-resolves under the new identity on its own.
   */
  const switchHere = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    if (!next.accounts.some((a) => a.userId === userId)) {
      return;
    }
    // Same ordering rule as `switchTo`: clear + re-prime BEFORE flipping the pin.
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    writeTabAccountId(userId);
  };

  /**
   * Opens the login screen in "add" mode to add another account WITHOUT
   * signing the current one out (Gmail / Microsoft To Do style). The `mode=add`
   * flag tells the auth flow to skip the pre-sign-out so the existing account
   * stays primed in the registry.
   */
  const addAccount = async () => {
    await navigate({ to: "/login", search: { redirect: "/", mode: "add" } });
  };

  /**
   * Activates the explicit guest account (visitor / local-only mode) in THIS
   * tab by pinning it. Other tabs are unaffected.
   */
  const continueAsGuest = async () => {
    const next = await accountsStore.ensureGuestActive();
    setState(next);
    const guest = getActiveAccount(next);
    if (!guest) {
      return;
    }
    queryClient.clear();
    primeSessionForAccount(guest);
    writeTabAccountId(guest.userId);
    // D5: Continuing as guest crosses a privilege boundary; safe landing at root is deliberate
    await navigate({ to: "/" });
  };

  /**
   * Removes an account from the registry and destroys its local database. If it
   * was THIS TAB's active account, we re-pin this tab to a remaining account.
   *
   * Other tabs need no repair: they are pinned by userId, so removing a
   * DIFFERENT account cannot move them (the positional-index scheme this
   * replaced needed `tab-account-pin.ts` for exactly that). A tab pinned to the
   * REMOVED account degrades to the default via `resolveTabAccount`.
   */
  const removeAccount = async (account: StoredAccount) => {
    const wasActive = activeTabAccountId() === account.userId;
    const next = await accountsStore.remove(account.userId);
    setState(next);
    // The alias map must not outlive the account it points at.
    pruneAliases(next.accounts.map((a) => a.userId));
    await destroyAccountData(account.userId);
    if (wasActive) {
      const fallback = getActiveAccount(next);
      queryClient.clear();
      primeSessionForAccount(fallback);
      if (fallback) {
        // Pin last, as everywhere else: the flip is what makes the reactive
        // consumers re-resolve, so it must come after the clear + re-prime.
        writeTabAccountId(fallback.userId);
        await navigate({ to: "/" });
      } else {
        clearTabAccountId();
        await navigate({ to: "/login" });
      }
    }
  };

  return {
    accounts,
    activeUserId,
    active,
    switchTo,
    switchHere,
    addAccount,
    continueAsGuest,
    removeAccount,
    reload,
  };
}
